/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */
import { getCookie, setCookie } from "@/utils/cookies";
import { getComponentElement, getTemplate } from "@/utils/dom/component";
import { MessageList, type MessageInterface } from "./message-list";

export class GlobalMessageList extends MessageList {
    static override selector = 'global-messages';
    static cookieName = 'mage-messages';

    private messageQueue: MessageInterface[] = [];

    constructor(element: HTMLElement | null) {
        super(element || document.createElement('div'));

        if (!element) {
            this.element = null as any;
            this.listElement = null as any;
            this.messageTemplate = null as any;
        }

        this.getCookieMessages();
    }

    /**
     * Set container element for global messages list
     */
    public setElement(element: HTMLElement) {
        this.element = element;

        this.listElement = this.element.querySelector<HTMLElement>(this.selectors.messageList)
            || this._createListElement();
        this.messageTemplate = getTemplate(MessageList.templateSelector, this.element)
            || this._createTemplate();

        if (this.messageQueue.length) {
            const messages = this.messageQueue.slice();
            this.messageQueue = [];
            this.addMessages(messages);
        }
    }

    /**
     * @inheritdoc
     */
    public override addMessage(message: MessageInterface): () => void {
        if (this.element) {
            return super.addMessage(message);
        }

        this.messageQueue.push(message);
        return () => this.dismissMessage(message);
    }

    /**
     * @inheritdoc
     */
    public override dismissMessage(message: MessageInterface): void {
        if (this.element) {
            super.dismissMessage(message);
            return;
        }

        this.messageQueue.splice(this.messageQueue.indexOf(message), 1);
    }

    public getCookieMessages(): void {
        const messagesData = getCookie(GlobalMessageList.cookieName);
        const messages = messagesData
            ? JSON.parse(decodeURIComponent(messagesData).replace(/\+/g, ' '))
            : [];

        this.addMessages(messages);

        setCookie(GlobalMessageList.cookieName, '', -1, true);
    }
}

export const globalMessageList = new GlobalMessageList(null);

export function initGlobalMessages() {
    const element = getComponentElement(GlobalMessageList.selector);
    element && globalMessageList.setElement(element);
}
